/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, useLayoutEffect, type RefObject, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { overlayHost } from "./overlayHost";
import { cn } from "../../lib/utils";

interface DropdownPanelProps {
  triggerRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  maxHeight?: number;
}

export function DropdownPanel({
  triggerRef,
  isOpen,
  children,
  className,
  maxHeight = 192,
}: DropdownPanelProps) {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setStyle(null);
      return;
    }

    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const viewport = window.visualViewport ?? { offsetTop: 0, height: window.innerHeight };
      const vpTop = viewport.offsetTop;
      const vpBottom = vpTop + viewport.height;

      const spaceBelow = vpBottom - rect.bottom;
      const spaceAbove = rect.top - vpTop;
      const goAbove = spaceBelow < maxHeight + 8 && spaceAbove > spaceBelow;

      const available = goAbove ? spaceAbove - 8 : spaceBelow - 8;
      const clampedMax = Math.min(maxHeight, Math.max(available, 60));

      const viewportWidth = document.documentElement.clientWidth;
      const maxWidth = Math.min(288, viewportWidth - 16);
      const nearRightEdge = rect.left + maxWidth > viewportWidth - 8;
      setStyle({
        position: "fixed",
        ...(nearRightEdge
          ? { right: viewportWidth - rect.right }
          : { left: rect.left }),
        minWidth: rect.width,
        maxWidth,
        width: "max-content",
        maxHeight: clampedMax,
        ...(goAbove
          ? { top: rect.top + vpTop - 4, transform: "translateY(-100%)" }
          : { top: rect.bottom + vpTop + 4 }),
      });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
    };
  }, [isOpen, triggerRef, maxHeight]);

  if (!isOpen || !style) return null;

  return createPortal(
    <div
      style={style}
      className={cn(
        "z-[9999] rounded-2xl border border-border bg-bg-elevated shadow-elevated",
        className
      )}
    >
      {children}
    </div>,
    overlayHost()
  );
}
